import { Component, OnDestroy, OnInit } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { Subject, take, takeUntil } from 'rxjs';
import { IProfileDetails } from 'src/app/core/domain/profile.model';
import { ConsentService } from 'src/app/core/service/consent.service';
import { getProfileData } from 'src/app/core/state/profile';
import * as ProfileActions from 'src/app/core/state/profile/profile.action';
import { ToastAlertService } from 'src/app/shared/utils/toast.service';

const DEFERRED_KEY = 'consentPromptDeferred';

/**
 * SAN-1479 (DPDP, decision D5) — one-time consent prompt for users created
 * after consent tracking shipped (`notificationSettings.consent.decided ===
 * false`). Both choices start unticked: nothing is sent on marketing,
 * summaries or WhatsApp until the user opts in. Existing users never see it.
 */
@Component({
  selector: 'app-consent-prompt',
  templateUrl: './consent-prompt.component.html',
})
export class ConsentPromptComponent implements OnInit, OnDestroy {
  visible = false;
  emailUpdates = false;
  whatsappUpdates = false;
  saving = false;
  private destroyed$ = new Subject<void>();

  constructor(
    private readonly store: Store,
    private readonly consentService: ConsentService,
    private readonly toastAlertService: ToastAlertService,
  ) { }

  ngOnInit(): void {
    this.store.pipe(select(getProfileData), takeUntil(this.destroyed$))
      .subscribe((profile: IProfileDetails) => {
        this.visible = profile?.notificationSettings?.consent?.decided === false && !this.isDeferred();
      });
  }

  save(): void {
    if (this.saving) return;
    this.saving = true;
    this.consentService.updateConsent({
      source: 'prompt',
      email: { marketing: this.emailUpdates, digest: this.emailUpdates, activity: true },
      whatsapp: {
        marketing: this.whatsappUpdates,
        digest: this.whatsappUpdates,
        activity: this.whatsappUpdates,
      },
    }).pipe(take(1)).subscribe({
      next: () => {
        this.saving = false;
        this.visible = false;
        this.store.dispatch(new ProfileActions.GetProfile());
      },
      error: () => {
        this.saving = false;
        this.toastAlertService.showToast('Could not save your choices. Please try again.', 'error');
      }
    });
  }

  /** Not deciding means no consent; ask again next session. */
  later(): void {
    this.visible = false;
    try {
      sessionStorage.setItem(DEFERRED_KEY, '1');
    } catch {
      /* storage unavailable: the prompt just shows again on the next profile load */
    }
  }

  private isDeferred(): boolean {
    try {
      return sessionStorage.getItem(DEFERRED_KEY) === '1';
    } catch {
      return false;
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
